import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { idiomaActual } from '@nucleo/i18n/idioma-actual';
import { ETIQUETAS_MENU_POR_CODIGO, TEXTOS_NUCLEO } from '@nucleo/i18n/textos-nucleo';
import { environment } from '@env';
import { NodoMenuDto, OpcionMenuDto, SeccionMenuDto } from './opcion-menu-dto';

const RUTA_MENU_USUARIO = '/account/menu';
const PREFIJO_ICONO = 'pi pi-';
const ICONO_POR_DEFECTO = 'pi pi-circle';
/** Las opciones de primer nivel sin hijos se agrupan en esta sección (su título se traduce). */
const IDENTIFICADOR_SECCION_PRINCIPAL = 'principal';

/**
 * Estado del menú lateral. Las opciones salen de `GET /account/menu` (ya filtradas por los permisos del
 * usuario y administrables desde Seguridad → Menú): cada grupo de primer nivel se muestra como una sección.
 * Las opciones propias del front (sin API) se filtran por rol con `filtrarPorRol`.
 */
@Injectable({ providedIn: 'root' })
export class NavegacionServicio {
  private readonly http = inject(HttpClient);

  /** Drawer de navegación en pantallas angostas (≤768px). */
  readonly menuMovilAbierto = signal(false);
  private readonly nodosMenu = signal<NodoMenuDto[]>([]);
  /** Secciones del menú del backend con etiquetas en el idioma activo (se recalculan al cambiar de idioma). */
  readonly seccionesDelBackend = computed(() => this.convertirASecciones(this.nodosMenu()));
  readonly cargandoMenu = signal(false);
  readonly errorMenu = signal(false);

  cargarMenu(): void {
    this.cargandoMenu.set(true);
    this.errorMenu.set(false);
    this.http
      .get<NodoMenuDto[]>(`${environment.apisBase.appWebComunia.url}${RUTA_MENU_USUARIO}`)
      .subscribe({
        next: (nodos) => {
          this.nodosMenu.set(nodos);
          this.cargandoMenu.set(false);
        },
        error: () => {
          this.nodosMenu.set([]);
          this.errorMenu.set(true);
          this.cargandoMenu.set(false);
        },
      });
  }

  abrirMenuMovil(): void {
    this.menuMovilAbierto.set(true);
  }

  cerrarMenuMovil(): void {
    this.menuMovilAbierto.set(false);
  }

  alternarMenuMovil(): void {
    this.menuMovilAbierto.update((abierto) => !abierto);
  }

  /** Devuelve solo las secciones/opciones visibles para los roles dados, descartando secciones vacías. */
  filtrarPorRol(secciones: SeccionMenuDto[], roles: readonly string[]): SeccionMenuDto[] {
    return secciones
      .map((seccion) => ({ ...seccion, opciones: this.filtrarOpciones(seccion.opciones, roles) }))
      .filter((seccion) => seccion.opciones.length > 0);
  }

  /** Aplana el árbol de secciones a sus opciones navegables (con ruta). */
  obtenerOpcionesNavegables(secciones: SeccionMenuDto[]): (OpcionMenuDto & { ruta: string })[] {
    const aplanar = (opciones: OpcionMenuDto[]): OpcionMenuDto[] =>
      opciones.flatMap((opcion) => (opcion.hijos ? aplanar(opcion.hijos) : [opcion]));
    return secciones
      .flatMap((seccion) => aplanar(seccion.opciones))
      .filter((opcion): opcion is OpcionMenuDto & { ruta: string } => !!opcion.ruta);
  }

  private convertirASecciones(nodos: NodoMenuDto[]): SeccionMenuDto[] {
    const sueltas = nodos.filter((nodo) => nodo.children.length === 0);
    const grupos = nodos.filter((nodo) => nodo.children.length > 0);
    const seccionPrincipal: SeccionMenuDto[] = sueltas.length
      ? [
          {
            identificador: IDENTIFICADOR_SECCION_PRINCIPAL,
            titulo: TEXTOS_NUCLEO[idiomaActual()].menu.seccionPrincipal,
            opciones: sueltas.map((nodo) => this.convertirAOpcion(nodo)),
          },
        ]
      : [];
    return [
      ...seccionPrincipal,
      ...grupos.map((grupo) => ({
        identificador: grupo.code,
        titulo: this.etiquetaTraducida(grupo),
        opciones: grupo.children.map((nodo) => this.convertirAOpcion(nodo)),
      })),
    ];
  }

  /** Etiqueta en el idioma activo por el `code` de la opción; si no se conoce, la que guarda el backend. */
  private etiquetaTraducida(nodo: NodoMenuDto): string {
    return ETIQUETAS_MENU_POR_CODIGO[idiomaActual()][nodo.code] ?? nodo.label;
  }

  private convertirAOpcion(nodo: NodoMenuDto): OpcionMenuDto {
    return {
      identificador: nodo.code,
      etiqueta: this.etiquetaTraducida(nodo),
      icono: nodo.icon ? `${PREFIJO_ICONO}${nodo.icon}` : ICONO_POR_DEFECTO,
      ruta: nodo.route ?? undefined,
      hijos: nodo.children.length ? nodo.children.map((hijo) => this.convertirAOpcion(hijo)) : undefined,
    };
  }

  private filtrarOpciones(opciones: OpcionMenuDto[], roles: readonly string[]): OpcionMenuDto[] {
    return opciones
      .filter(
        (opcion) =>
          !opcion.rolesPermitidos || opcion.rolesPermitidos.some((rol) => roles.includes(rol)),
      )
      .map((opcion) =>
        opcion.hijos ? { ...opcion, hijos: this.filtrarOpciones(opcion.hijos, roles) } : opcion,
      )
      .filter((opcion) => !opcion.hijos || opcion.hijos.length > 0);
  }
}
